import { useIsMountedRef } from 'hooks'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Typography, FormControl, FormControlLabel, Radio, RadioGroup, Link } from '@mui/material'
import { faWindowClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Button from 'components/Common/Button'
import { DISABLED_BUTTON, PRIMARY_BUTTON, SECONDARY_BUTTON } from 'themes/theme'
import Box from '@mui/material/Box'

import Dialog from '@mui/material/Dialog'
import { useStripe } from '@stripe/react-stripe-js'

import { Field, Form, Formik } from 'formik'
import { log } from 'helpers'
import * as yup from 'yup'

import TextField from 'components/Common/TextField'
import ZendeskLink from 'components/Common/ZendeskLink'

import CardField from './CardField'
import PromoCodeField from './PromoCodeField'
import SuccessModal from './SuccessModal'
import PaymentFailModal from './PaymentFailModal'

import api from 'api'

const sx = {
  dialogWrapper: {
    color: '#25245F',
    padding: '30px 40px',
    fontWeight: 'normal',
    display: 'flex',
    flexDirection: 'column',
  },
  radioButton: {
    '& svg': {
      width: '2em',
      height: '2em',
    },
  },
  dialogWidth: { maxWidth: '1030px', width: '100%' },
  withBorder: { borderBottom: '1px solid #E8E8E8', paddingLeft: '10px' },
  backButton: {
    padding: '10px 30px',
    borderRadius: '50px',
    fontWeight: 'bold',
    backgroundColor: '#ABABAB !important',
    borderColor: 'transparent',
    boxShadow: 'none',
    marginRight: '10px',
    '&:hover, &:focus': {
      backgroundColor: '#ABABAB !important',
      borderColor: '#ABABAB !important',
      boxShadow: 'none',
    },
    '&:disabled': {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
  },
  submitButton: {
    padding: '10px 30px',
    borderRadius: '50px',
    fontWeight: 'bold',
    backgroundColor: 'var(--purple) !important',
    borderColor: 'var(--purple) !important',
    '&:hover': {
      backgroundColor: 'var(--purple) !important',
      borderColor: 'var(--purple) !important',
    },
    '&:disabled': {
      backgroundColor: 'var(--purple) !important',
      borderColor: 'var(--purple) !important',
    },
  },
  buttonBack: {
    color: '#0978C5',
    fontWeight: 'bold',
    '&:hover': {
      color: 'rgba(9,120,197, 0.8)',
    },
  },
  boxPrice: {
    position: 'relative',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '1px solid #C6D6DA',
    backgroundColor: '#F5F9FA',
    width: '420px',
    padding: '25px',
    borderRadius: '5px',
  },
  boxPromo: {
    display: 'flex',
    alignItems: 'center',
    borderRight: '1px solid #E8E8E8',
    borderLeft: '1px solid #E8E8E8',
  },
  boxSubmitPayment: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    marginTop: 10,
  },
  boxError: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    '& h2': {
      marginTop: '100px',
    },
  },
  promotionText: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '80px',
    height: '25px',
    background: '#00E2BC',
    fontSize: '12px',
    borderRadius: '2px',
    position: 'absolute',
    zIndex: '99',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    right: '-9px',
    top: '4px',
    transform: 'rotate(22deg)',
  },
  link: {
    color: '#0A78CD',
    fontWeight: 'bold',
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  leftSection: {
    minHeight: '423px',
  },
}

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  zip: yup
    .string()
    .matches(/^\d{5}(?:[-\s]\d{4})?$/, 'Please, fill Zip Code in correct format')
    .required('Zip Code is required'),
})

const PLANS_SUBSCRIPTION = [
  {
    value: 49.99,
    period: 'mo',
    title: 'Monthly',
    displayText: '49.99',
    promotionText: null,
    stripeId: process.env['REACT_APP_SUBSCRIPTION_STRIPE_ID_MONTH'],
  },
  {
    value: 479.9,
    promotionText: 'Save 119!',
    displayText: '479.90',
    period: 'yr',
    title: 'Annually',
    stripeId: process.env['REACT_APP_SUBSCRIPTION_STRIPE_ID_ANNUAL'],
  },
]

const SubscriptionModal = props => {
  const formik = useRef(null)
  const stripe = useStripe()
  const dispatch = useDispatch()
  const [step, setStep] = useState(1)
  const [planSelected, setPlanSelected] = useState(PLANS_SUBSCRIPTION[0])
  const [price, setPrice] = useState(PLANS_SUBSCRIPTION[0])

  const [cardElement, setCardElement] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [error, setError] = useState()
  const [isOpenSuccessModal, setIsOpenSuccessModal] = useState(false)
  const isMountedRef = useIsMountedRef()
  const [stripePromotionCode, setStripePromotionCode] = useState(null)
  const [, setCodeCheckLoading] = useState(false)
  const [isPaymentFailModalOpen, setIsPaymentFailModalOpen] = useState(false)
  const { user } = useSelector(state => state.userReducer)
  const [eros, setEros] = useState(false)
  const [value, setValue] = useState('')
  const inValidPromo =
    stripePromotionCode === null ||
    (planSelected?.title === 'Annually' && stripePromotionCode?.metadata?.SubscriptionType === 'monthly') ||
    stripePromotionCode?.active === false

  const totalAmount = inValidPromo
    ? price?.value
    : ((price?.value || 0) * (100 - stripePromotionCode?.coupon?.percent_off)) / 100

  const cardCompleted = cardElement !== null

  const buttonDisabled = !!error || !cardCompleted

  useEffect(() => {
    if (totalAmount === 0) {
      setError('')
    }
  }, [totalAmount])

  useEffect(() => {
    setPrice(planSelected)
  }, [planSelected])

  useEffect(() => {
    if (isOpenSuccessModal === true) {
      document.documentElement.style.overflow = 'hidden'
    }
    return () => {
      document.documentElement.style.overflow = null
    }
  }, [isOpenSuccessModal])

  const onCloseModal = useCallback(() => {
    props.setModalOpen(false)
    setStep(1)
  }, [props])

  const handleNextStep = () => {
    setStep(step => (step += 1))
  }

  const handlePrevStep = () => {
    if (step === 1) return onCloseModal()
    setStep(step => (step -= 1))
    setStripePromotionCode(null)
    valueSet('')
  }

  const handleCloseSuccessModal = () => {
    setIsOpenSuccessModal(false)
  }

  const onCardFieldComplete = useCallback(cardElement => {
    setCardElement(cardElement)
  }, [])

  const onCode = useCallback(stripePromotionCode => {
    setStripePromotionCode(stripePromotionCode)
  }, [])

  const valueSet = useCallback(value => {
    setValue(value)
  }, [])

  const errorSet = useCallback(error => {
    setEros(error)
  }, [])

  const onCodeCheckLoading = useCallback(state => {
    setCodeCheckLoading(state)
  }, [])

  const onFormSubmit = useCallback(
    async (values, formikHelpers) => {
      delete values.accountType
      if (user && price && stripe) {
        if (stripePromotionCode === null && totalAmount === 0) {
          formikHelpers.setSubmitting(true)
          try {
            await api.updateMyAccountProfile(values)
            await api.createSubscription(undefined, price.stripeId, stripePromotionCode.id)

            setIsOpenSuccessModal(true)
            onCloseModal()
          } catch (error) {
            setStep(3)
            setSubmitError(error.message)
          } finally {
            const user = await api.getUser()
            dispatch({ type: 'userUpdated', user })
            if (isMountedRef.current) {
              formikHelpers.setSubmitting(false)
            }
          }
        } else if (cardElement) {
          formikHelpers.setSubmitting(true)

          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: { email: user.email },
          })

          if (error || !paymentMethod) {
            if (isMountedRef.current) {
              formikHelpers.setSubmitting(false)
            }

            setIsPaymentFailModalOpen(true)
            return
          }
          try {
            await api.updateMyAccountProfile(values)
            await api.createSubscription(
              paymentMethod.id,
              price.stripeId,
              stripePromotionCode ? stripePromotionCode.id : '',
            )
            setIsOpenSuccessModal(true)
            onCloseModal()
          } catch (error) {
            setStep(3)
            setSubmitError(
              error.response.data.error_message.includes(
                'This promotion code cannot be redeemed because the associated customer has prior transactions.',
              )
                ? 'This promo code has already been used.'
                : error.message,
            )
          } finally {
            const user = await api.getUser()
            dispatch({ type: 'userUpdated', user })
            if (isMountedRef.current) {
              formikHelpers.setSubmitting(false)
            }
          }
        }
      } else {
        log.info('No user && product && price && stripe', user, price, stripe)
      }
    },
    [cardElement, price, stripePromotionCode, user, dispatch, onCloseModal, stripe, isMountedRef, totalAmount],
  )

  const renderContentStep1 = () => {
    return (
      <div className="row px-4">
        <div className="col-12 mt-5 d-flex justify-content-center">
          <FormControl>
            <RadioGroup name="plans_subscription" defaultValue={planSelected.title}>
              {PLANS_SUBSCRIPTION.map(item => (
                <div key={item.stripeId} className="col-12">
                  <Box sx={sx.boxPrice}>
                    {item.promotionText && <Box sx={sx.promotionText}>{item.promotionText}</Box>}
                    <div className="d-flex align-items-center">
                      <FormControlLabel
                        sx={sx.radioButton}
                        value={item.title}
                        onChange={() => {
                          setPlanSelected(item)
                        }}
                        control={<Radio style={{ color: 'var(--primary)' }} />}
                      />
                      <div className="text-bold text-title m-0">{item.title}</div>
                    </div>
                    <div className="wrapper-price">
                      <span className="text-bold text-title color-secondary">${item.displayText}</span>
                      <span className="text-bold text-normal color-secondary">/ {item.period}.</span>
                    </div>
                  </Box>
                </div>
              ))}
            </RadioGroup>
          </FormControl>
        </div>

        <div />
      </div>
    )
  }

  const renderContentStep2 = () => {
    return (
      <>
        <div className="row text-center">
          <Box component="h2" sx={sx.withBorder} className={`pb-4 text-bold`}>
            Markaaz Premium Subscription
          </Box>
          <span className="text-normal">
            Your subscription will renew automatically unless cancelled. View your account after purchase for details.
          </span>
        </div>

        <Formik
          innerRef={formik}
          validationSchema={schema}
          onSubmit={onFormSubmit}
          initialValues={user}
          enableReinitialize
        >
          {({ dirty, isSubmitting, values, setFieldValue, isValid }) => {
            return (
              <Form>
                <div className="row mt-4">
                  <div className="col-md-9">
                    <div className="row">
                      <Box className="col-md-7 customAuthInput" sx={sx.leftSection}>
                        <div className="row  mb-3">
                          <div className="col-md-6">
                            <Field name="firstName">
                              {({ field, meta }) => (
                                <TextField
                                  placeholder={'First Name on Card'}
                                  error={Boolean(meta.touched && meta.error)}
                                  helperText={meta.touched && meta.error}
                                  {...field}
                                  label="First Name"
                                  fullWidth
                                  bgwhite
                                  required
                                />
                              )}
                            </Field>
                          </div>
                          <div className="col-md-6">
                            <Field name="lastName">
                              {({ field, meta }) => (
                                <TextField
                                  placeholder={'Last Name on Card'}
                                  error={Boolean(meta.touched && meta.error)}
                                  helperText={meta.touched && meta.error}
                                  {...field}
                                  label="Last Name"
                                  fullWidth
                                  bgwhite
                                  required
                                />
                              )}
                            </Field>
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-12">
                            <Field name="address">
                              {({ field, meta }) => (
                                <TextField
                                  placeholder={'Address'}
                                  error={Boolean(meta.touched && meta.error)}
                                  helperText={meta.touched && meta.error}
                                  {...field}
                                  label="Address"
                                  fullWidth
                                  bgwhite
                                  required
                                />
                              )}
                            </Field>
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <Field name="city">
                              {({ field, meta }) => (
                                <TextField
                                  placeholder={'Billing City'}
                                  error={Boolean(meta.touched && meta.error)}
                                  helperText={meta.touched && meta.error}
                                  {...field}
                                  label="City"
                                  fullWidth
                                  bgwhite
                                  required
                                />
                              )}
                            </Field>
                          </div>
                          <div className="col-md-6">
                            <Field name="state">
                              {({ field, meta }) => (
                                <TextField
                                  placeholder={'Billing State'}
                                  error={Boolean(meta.touched && meta.error)}
                                  helperText={meta.touched && meta.error}
                                  {...field}
                                  label="State"
                                  fullWidth
                                  bgwhite
                                  required
                                />
                              )}
                            </Field>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <Field name="zip">
                              {({ field, meta }) => (
                                <TextField
                                  placeholder={'Zip Code'}
                                  error={Boolean(meta.touched && meta.error)}
                                  helperText={meta.touched && meta.error}
                                  {...field}
                                  label="Zip Code"
                                  fullWidth
                                  bgwhite
                                  required
                                />
                              )}
                            </Field>
                          </div>
                        </div>
                      </Box>

                      <Box sx={sx.boxPromo} className={`col-md-5`}>
                        <div className="w-full">
                          <div className="w-full customAuthInput">
                            <PromoCodeField
                              onCode={onCode}
                              errorSet={errorSet}
                              onLoading={onCodeCheckLoading}
                              planSelected={planSelected}
                              stripePromotionCode={stripePromotionCode}
                              value={value}
                              valueSet={valueSet}
                            />
                          </div>
                          <div className="w-full">
                            <CardField required onComplete={onCardFieldComplete} />
                          </div>
                        </div>
                      </Box>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <Box sx={sx.boxSubmitPayment} className={`text-center`}>
                      <div className="wrapper-submit-price mb-3">
                        <span style={{ fontSize: '25px', fontWeight: '900' }}>
                          ${(Math.trunc(totalAmount * 100) / 100)?.toFixed(2)}{' '}
                        </span>
                        <span style={{ textTransform: 'lowercase', fontSize: 25, fontWeight: 400 }}>
                          {planSelected.title}
                        </span>
                      </div>
                      <div className="k-form-buttons k-justify-content-center mt-auto">
                        <Button
                          fullWidth
                          submit
                          type={
                            !isValid || buttonDisabled || eros || (inValidPromo && value !== '')
                              ? DISABLED_BUTTON
                              : PRIMARY_BUTTON
                          }
                          disabled={buttonDisabled || isSubmitting}
                          loading={isSubmitting}
                        >
                          Submit Payment
                        </Button>
                      </div>
                    </Box>
                    <div style={{ marginTop: '10px', padding: '10px' }}>
                      <Typography variant="body2" sx={{ fontSize: '12px' }}>
                        By clicking Submit Payment, I acknowledge that I have read, understand, and agree to the
                        Markaaz&nbsp;
                        <Link
                          rel="noreferrer"
                          href="https://markaaz.com/terms-and-conditions/?_gl=1*1rkybce*_ga*MTQ3OTE4MTIyMC4xNjMyMzE3MzA3*_ga_ERS8LY82HR*MTY0MjYxNjM5MS4yMDIuMC4xNjQyNjE2MzkxLjYw"
                          target="_blank"
                          underline="hover"
                        >
                          <Typography component="span" sx={{ fontSize: '12px' }} variant="body2B" color="secondary">
                            Terms of Use
                          </Typography>
                        </Link>
                        , including the arbitration, limitation of liability, and class waiver provisions.
                      </Typography>
                    </div>
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </>
    )
  }

  const renderContentStep3 = error => {
    return (
      <Box sx={sx.boxError} className={`row text-center`}>
        <Box>
          <Box component="h2" sx={sx.title}>
            {error === 'This promo code has already been used.' ? error : "Your payment couldn't be processed."}
          </Box>
        </Box>
        <div>
          <div>
            <FontAwesomeIcon
              icon={faWindowClose}
              style={{ width: '120px', height: '120px', fill: 'red', color: 'red' }}
            />
          </div>
          <Box component="h2" sx={sx.title} className={`pb-4 mt-4`}>
            {error
              ? 'We’ll take you back to checkout so you can try again.'
              : 'Thank you for becoming a premium member of the Markaaz network!'}
          </Box>
        </div>
      </Box>
    )
  }

  return (
    <>
      <div>
        <Dialog
          open={props.isModalOpen}
          PaperProps={{ sx: { width: sx.dialogWidth, marginTop: 12, maxHeight: '80%' } }}
          onClose={onCloseModal}
        >
          <span
            class="k-icon k-i-x k-cursor-pointer modal-close2"
            style={{ top: -15, right: -12 }}
            onClick={handlePrevStep}
          ></span>
          <Box sx={sx.dialogWrapper}>
            {step !== 3 && (
              <div>
                <p className="text-title k-mb-4 d-block text-center">Step {step} of 2</p>
                <Box component="h2" sx={sx.title} className={`text-center text-bold`}>
                  {step === 1 ? 'Your Subscription: Premium' : 'Payment'}
                </Box>
              </div>
            )}

            {step === 1 && renderContentStep1()}
            {step === 2 && renderContentStep2()}
            {step === 3 && renderContentStep3(submitError)}

            <div className="row mt-4">
              <div className="col-md-12">
                <div className="d-flex d-flex justify-content-center">
                  {step === 1 && (
                    <>
                      <Button type={SECONDARY_BUTTON} style={{ width: 150, marginRight: 20 }} onClick={handlePrevStep}>
                        Close
                      </Button>
                      <Button type={PRIMARY_BUTTON} style={{ width: 150 }} onClick={handleNextStep}>
                        Pay
                      </Button>
                    </>
                  )}
                  {step === 2 && (
                    <Button type={SECONDARY_BUTTON} onClick={handlePrevStep} style={{ width: 150 }}>
                      Back
                    </Button>
                  )}
                  {step === 3 && (
                    <Button type={PRIMARY_BUTTON} sx={{ root: sx.submitButton }} onClick={handlePrevStep}>
                      Try again
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <ZendeskLink />
            </div>
          </Box>
        </Dialog>
      </div>
      <SuccessModal
        open={isOpenSuccessModal}
        onClose={handleCloseSuccessModal}
        subTitle="Thank you for becoming a premium member of Markaaz!"
      />
      <PaymentFailModal open={isPaymentFailModalOpen} onClose={() => setIsPaymentFailModalOpen(false)} />
    </>
  )
}

export default SubscriptionModal
